import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, type Role, type UserRow } from '../../lib/api';
import { Drawer } from '@allrouted/ui/Drawer';
import { Select } from '@allrouted/ui/Select';
import { Menu, MenuItem, KebabIcon } from '@allrouted/ui/Menu';
import { IconButton, RefreshIcon } from '@allrouted/ui/Icons';
import { useUi } from '@allrouted/ui/Toaster';
import { useAuth } from '../AuthGate';
import { Reveal, ago, errText, fullTime } from './AccountView';

/**
 * Everybody who can sign in to this account.
 *
 * Adding somebody emails them an invitation to choose their own password:
 * nobody reads a password down the phone, and the address is proven by the
 * same click. Setting a password for them is still there for the person
 * whose email will not arrive, under the row's menu.
 */

export const ROLES: { value: Role; label: string; hint: string }[] = [
  { value: 'owner', label: 'Owner', hint: 'Everything, including billing. The only role that can make another owner.' },
  { value: 'admin', label: 'Admin', hint: 'People, API keys and settings. Cannot touch billing or make an owner.' },
  { value: 'dispatcher', label: 'Dispatcher', hint: 'The day itself: plans, routes, orders, depots, vehicles and drivers.' },
  { value: 'viewer', label: 'Viewer', hint: 'Sees everything and changes nothing.' },
];

const roleLabel = (role: Role) => ROLES.find((r) => r.value === role)?.label ?? role;

const STATUS: Record<UserRow['status'], { label: string; tone: string }> = {
  active: { label: 'Active', tone: 'ok' },
  invited: { label: 'Invited', tone: 'info' },
  disabled: { label: 'Disabled', tone: 'idle' },
};

function initials(u: { name: string | null; email: string }): string {
  const source = u.name?.trim() || u.email.split('@')[0] || '?';
  const parts = source.split(/[\s._-]+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || '?';
}

/** Every address in a pasted list: commas, semicolons, spaces or new lines. */
function parseEmails(raw: string): { valid: string[]; invalid: string[] } {
  const items = [...new Set(raw.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean))];
  const ok = (s: string) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(s);
  return { valid: items.filter(ok), invalid: items.filter((s) => !ok(s)) };
}

export function PeoplePage() {
  const ui = useUi();
  const { me } = useAuth();
  const myRole = me.user?.role;
  const canManage = myRole === 'owner' || myRole === 'admin';

  const [users, setUsers] = useState<UserRow[] | null>(null);
  const [query, setQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [inviting, setInviting] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [issued, setIssued] = useState<{ email: string; password: string } | null>(null);

  const load = useCallback(async () => {
    try {
      setUsers((await api.users()).users);
    } catch (err) {
      ui.notify('error', 'Could not load people', errText(err));
      setUsers([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (users ?? []).filter(
      (u) =>
        (roleFilter === 'all' || u.role === roleFilter) &&
        (statusFilter === 'all' || u.status === statusFilter) &&
        (!q || u.email.toLowerCase().includes(q) || (u.name ?? '').toLowerCase().includes(q)),
    );
  }, [users, query, roleFilter, statusFilter]);

  const counts = useMemo(() => {
    const c = { active: 0, invited: 0, disabled: 0 };
    for (const u of users ?? []) c[u.status] += 1;
    return c;
  }, [users]);

  /** What I may do to this person: the owner rule, and nothing to yourself. */
  const mayAct = (u: UserRow) => canManage && u.id !== me.user?.id && !(u.role === 'owner' && myRole !== 'owner');

  const run = async (fn: () => Promise<unknown>, ok: string, body?: string) => {
    try {
      await fn();
      ui.notify('ok', ok, body);
      await load();
    } catch (err) {
      ui.notify('error', 'That did not work', errText(err));
    }
  };

  const actions = {
    resend: (u: UserRow) => run(() => api.resendInvite(u.id), 'Invitation sent', `A fresh link went to ${u.email}.`),
    reset: (u: UserRow) => run(() => api.sendResetLink(u.id), 'Reset link sent', `${u.email} can choose a new password from the email.`),
    signOut: async (u: UserRow) => {
      const ok = await ui.confirm({
        title: `Sign ${u.name || u.email} out everywhere?`,
        body: 'Every browser they are signed in on is signed out at once. They can sign straight back in.',
        confirmLabel: 'Sign out everywhere',
      });
      if (ok) await run(() => api.signOutUser(u.id), 'Signed out', `${u.email} has no open sessions now.`);
    },
    generate: async (u: UserRow) => {
      const ok = await ui.confirm({
        title: `Generate a password for ${u.email}?`,
        body: 'For when email will not reach them. A new password is shown to you once, and any session they hold ends.',
        confirmLabel: 'Generate password',
      });
      if (!ok) return;
      try {
        setIssued(await api.setUserPassword(u.id));
        setOpenId(null);
        await load();
      } catch (err) {
        ui.notify('error', 'Could not set a password', errText(err));
      }
    },
    setStatus: async (u: UserRow, status: 'active' | 'disabled') => {
      if (status === 'disabled') {
        const ok = await ui.confirm({
          title: `Disable ${u.name || u.email}?`,
          body: 'They are signed out and cannot sign in until somebody turns them back on. Nothing they did is lost.',
          confirmLabel: 'Disable',
          danger: true,
        });
        if (!ok) return;
      }
      await run(() => api.updateUser(u.id, { status }), status === 'disabled' ? 'Disabled' : 'Turned back on');
    },
    remove: async (u: UserRow) => {
      const ok = await ui.confirm({
        title: `Remove ${u.name || u.email}?`,
        body: 'They lose access immediately and are taken off this account. The history of what they changed stays.',
        confirmLabel: 'Remove',
        danger: true,
      });
      if (ok) {
        setOpenId(null);
        await run(() => api.removeUser(u.id), 'Removed', u.email);
      }
    },
  };

  const open = users?.find((u) => u.id === openId) ?? null;

  return (
    <>
      {issued ? (
        <Reveal
          title={`Give this password to ${issued.email}. It is not shown again.`}
          body="They should change it under Your profile once they are in. You can always generate another; this one cannot be recovered."
          value={issued.password}
          onDone={() => setIssued(null)}
        />
      ) : null}

      <section className="acct-card">
        <div className="acct-toolbar">
          <input
            className="field acct-search"
            type="search"
            placeholder="Search by name or email"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <Select
            value={roleFilter}
            options={[{ value: 'all', label: 'All roles' }, ...ROLES.map((r) => ({ value: r.value, label: r.label }))]}
            onChange={setRoleFilter}
          />
          <Select
            value={statusFilter}
            options={[
              { value: 'all', label: 'Any status' },
              { value: 'active', label: `Active (${counts.active})` },
              { value: 'invited', label: `Invited (${counts.invited})` },
              { value: 'disabled', label: `Disabled (${counts.disabled})` },
            ]}
            onChange={setStatusFilter}
          />
          <span className="spacer" />
          <IconButton label="Refresh" onClick={() => void load()}>
            <RefreshIcon />
          </IconButton>
          {canManage ? (
            <button type="button" className="btn btn-primary" onClick={() => setInviting(true)}>
              Invite people
            </button>
          ) : null}
        </div>

        {users === null ? (
          <p className="hint acct-empty">Loading…</p>
        ) : shown.length === 0 ? (
          <p className="hint acct-empty">{users.length ? 'Nobody matches.' : 'Nobody here yet.'}</p>
        ) : (
          <div className="table-scroll">
            <table className="grid acct-table">
              <thead>
                <tr>
                  <th>Person</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Signs in with</th>
                  <th>Last active</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {shown.map((u) => (
                  <tr key={u.id} className="acct-row" onClick={() => setOpenId(u.id)}>
                    <td>
                      <div className="person">
                        <span className="person-avatar" aria-hidden="true">
                          {initials(u)}
                        </span>
                        <span className="person-text">
                          <strong>
                            {u.name || u.email.split('@')[0]}
                            {u.id === me.user?.id ? <span className="you-tag">You</span> : null}
                          </strong>
                          <span className="dim">{u.email}</span>
                        </span>
                      </div>
                    </td>
                    <td>{roleLabel(u.role)}</td>
                    <td>
                      <span className="pill" data-tone={STATUS[u.status].tone}>
                        {STATUS[u.status].label}
                      </span>
                      {u.status === 'invited' && u.invited_at ? (
                        <span className="dim small acct-sub" title={fullTime(u.invited_at)}>
                          sent {ago(u.invited_at).toLowerCase()}
                        </span>
                      ) : null}
                    </td>
                    <td className="dim">{signInMethods(u)}</td>
                    <td className="dim" title={fullTime(u.last_seen_at ?? u.last_login_at)}>
                      {ago(u.last_seen_at ?? u.last_login_at)}
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      {mayAct(u) ? (
                        <Menu label={`Actions for ${u.email}`} icon={<KebabIcon />} align="right">
                          <MenuItem label="View details" onSelect={() => setOpenId(u.id)} />
                          {u.status === 'invited' ? (
                            <MenuItem label="Send invitation again" onSelect={() => void actions.resend(u)} />
                          ) : null}
                          {u.status === 'active' ? (
                            <MenuItem label="Email a password-reset link" onSelect={() => void actions.reset(u)} />
                          ) : null}
                          <MenuItem label="Generate a password" hint="When email will not reach them" onSelect={() => void actions.generate(u)} />
                          {u.sessions > 0 ? (
                            <MenuItem label="Sign out everywhere" onSelect={() => void actions.signOut(u)} />
                          ) : null}
                          {u.status === 'active' ? (
                            <MenuItem label="Disable" danger onSelect={() => void actions.setStatus(u, 'disabled')} />
                          ) : null}
                          {u.status === 'disabled' ? (
                            <MenuItem label="Turn back on" onSelect={() => void actions.setStatus(u, 'active')} />
                          ) : null}
                          <MenuItem label="Remove from account" danger onSelect={() => void actions.remove(u)} />
                        </Menu>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="acct-card">
        <h2 className="acct-card-title">What each role can do</h2>
        <dl className="role-legend">
          {ROLES.map((r) => (
            <div key={r.value}>
              <dt>{r.label}</dt>
              <dd>{r.hint}</dd>
            </div>
          ))}
        </dl>
      </section>

      {inviting ? (
        <InviteDrawer
          onClose={() => setInviting(false)}
          onDone={async (password) => {
            setInviting(false);
            if (password) setIssued(password);
            await load();
          }}
        />
      ) : null}

      {open ? (
        <PersonDrawer
          user={open}
          isMe={open.id === me.user?.id}
          mayAct={mayAct(open)}
          onClose={() => setOpenId(null)}
          onChanged={() => void load()}
          actions={actions}
        />
      ) : null}
    </>
  );
}

function signInMethods(u: UserRow): string {
  if (u.status === 'invited' && !u.has_password && !u.has_google) return 'Not yet';
  const m = [u.has_password ? 'Password' : null, u.has_google ? 'Google' : null].filter(Boolean);
  return m.length ? m.join(' · ') : '—';
}

/* ------------------------------------------------------------------ *
 * Inviting
 * ------------------------------------------------------------------ */

function RolePicker({ value, onChange, allowOwner }: { value: Role; onChange: (r: Role) => void; allowOwner: boolean }) {
  return (
    <div className="role-picker" role="radiogroup" aria-label="Role">
      {ROLES.filter((r) => allowOwner || r.value !== 'owner').map((r) => (
        <button
          key={r.value}
          type="button"
          role="radio"
          aria-checked={value === r.value}
          className="role-option"
          onClick={() => onChange(r.value)}
        >
          <span className="role-radio" aria-hidden="true" />
          <span>
            <strong>{r.label}</strong>
            <small>{r.hint}</small>
          </span>
        </button>
      ))}
    </div>
  );
}

function generatePassword(): string {
  const bytes = new Uint8Array(18);
  crypto.getRandomValues(bytes);
  return btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function InviteDrawer({
  onClose,
  onDone,
}: {
  onClose: () => void;
  onDone: (password: { email: string; password: string } | null) => Promise<void>;
}) {
  const ui = useUi();
  const { me } = useAuth();
  const [emails, setEmails] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<Role>('dispatcher');
  const [withPassword, setWithPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const parsed = parseEmails(emails);
  const single = parsed.valid.length <= 1;

  const submit = async () => {
    if (!parsed.valid.length) {
      ui.notify('info', 'Add an email address', 'One per line, or separated by commas.');
      return;
    }
    if (parsed.invalid.length) {
      ui.notify('error', 'Check the addresses', `These do not look right: ${parsed.invalid.join(', ')}`);
      return;
    }
    if (withPassword && password.length < 10) {
      ui.notify('error', 'Password too short', 'Ten characters at least. Generate one.');
      return;
    }
    setBusy(true);
    const added: string[] = [];
    const notSent: string[] = [];
    const failed: string[] = [];
    for (const email of parsed.valid) {
      try {
        const r = await api.addUser({
          email,
          name: single && name.trim() ? name.trim() : undefined,
          role,
          password: withPassword && single ? password : undefined,
        });
        added.push(email);
        if (r.invite && !r.invite.sent) notSent.push(email);
      } catch (err) {
        failed.push(`${email}: ${errText(err)}`);
      }
    }
    setBusy(false);

    if (failed.length) ui.notify('error', `${failed.length} not added`, failed.join('\n'));
    if (notSent.length) {
      ui.notify(
        'error',
        'Added, but the invitation email did not go',
        `${notSent.join(', ')}. Try “Send invitation again” from their row, or generate a password for them.`,
      );
    }
    if (added.length && !notSent.length) {
      ui.notify(
        'ok',
        withPassword && single ? 'Added' : added.length === 1 ? 'Invitation sent' : `${added.length} invitations sent`,
        withPassword && single ? 'They can sign in now with the password below.' : 'They choose a password from the email, and are in.',
      );
    }
    if (added.length || !failed.length) {
      await onDone(withPassword && single && added.length ? { email: parsed.valid[0]!, password } : null);
    }
  };

  return (
    <Drawer
      open
      title="Invite people"
      subtitle="They get an email with a link to choose a password."
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={() => void submit()} disabled={busy || !parsed.valid.length}>
            {busy
              ? 'Adding…'
              : withPassword && single
                ? 'Add with password'
                : parsed.valid.length > 1
                  ? `Send ${parsed.valid.length} invitations`
                  : 'Send invitation'}
          </button>
        </>
      }
    >
      <div className="drawer-form">
        <label className="stack">
          <span className="label">Email addresses</span>
          <textarea
            className="field field-area"
            rows={3}
            value={emails}
            placeholder={'sam@company.com\nalex@company.com'}
            onChange={(e) => setEmails(e.target.value)}
            autoFocus
          />
          <span className="hint">
            {parsed.valid.length > 1
              ? `${parsed.valid.length} people, all with the role below.`
              : 'Several at once: one per line, or separated by commas.'}
            {parsed.invalid.length ? <span className="bad"> Not an address: {parsed.invalid.join(', ')}</span> : null}
          </span>
        </label>

        {single ? (
          <label className="stack">
            <span className="label">Name</span>
            <input className="field" value={name} placeholder="Optional. They can add it themselves." onChange={(e) => setName(e.target.value)} />
          </label>
        ) : null}

        <div className="stack">
          <span className="label">Role</span>
          <RolePicker value={role} onChange={setRole} allowOwner={me.user?.role === 'owner'} />
        </div>

        {single ? (
          <div className="stack">
            <button
              type="button"
              className="toggle"
              role="switch"
              aria-checked={withPassword}
              onClick={() => {
                setWithPassword((v) => !v);
                if (!password) setPassword(generatePassword());
              }}
            >
              <span className="toggle-track">
                <span className="toggle-knob" />
              </span>
              <span className="toggle-label">Set a password instead of emailing an invitation</span>
            </button>
            {withPassword ? (
              <>
                <div className="field-pair">
                  <input className="field mono" value={password} onChange={(e) => setPassword(e.target.value)} />
                  <button type="button" className="btn btn-small" onClick={() => setPassword(generatePassword())}>
                    Generate
                  </button>
                </div>
                <span className="hint">You will see it once to pass on. They can sign in immediately and should change it.</span>
              </>
            ) : null}
          </div>
        ) : null}
      </div>
    </Drawer>
  );
}

/* ------------------------------------------------------------------ *
 * One person
 * ------------------------------------------------------------------ */

function PersonDrawer({
  user,
  isMe,
  mayAct,
  onClose,
  onChanged,
  actions,
}: {
  user: UserRow;
  isMe: boolean;
  mayAct: boolean;
  onClose: () => void;
  onChanged: () => void;
  actions: {
    resend: (u: UserRow) => Promise<void>;
    reset: (u: UserRow) => Promise<void>;
    signOut: (u: UserRow) => Promise<void>;
    generate: (u: UserRow) => Promise<void>;
    setStatus: (u: UserRow, s: 'active' | 'disabled') => Promise<void>;
    remove: (u: UserRow) => Promise<void>;
  };
}) {
  const ui = useUi();
  const { me } = useAuth();
  const [name, setName] = useState(user.name ?? '');
  const [role, setRole] = useState<Role>(user.role);
  const [saving, setSaving] = useState(false);
  const dirty = name.trim() !== (user.name ?? '') || role !== user.role;
  /* You may rename yourself; everything else about you is somebody else's call. */
  const canEditName = mayAct || isMe;

  const save = async () => {
    setSaving(true);
    try {
      await api.updateUser(user.id, {
        ...(name.trim() !== (user.name ?? '') ? { name: name.trim() } : {}),
        ...(role !== user.role ? { role } : {}),
      });
      ui.notify('ok', 'Saved');
      onChanged();
    } catch (err) {
      ui.notify('error', 'Not saved', errText(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open
      title={user.name || user.email}
      subtitle={user.name ? user.email : undefined}
      onClose={onClose}
      footer={
        canEditName ? (
          <>
            <button type="button" className="btn" onClick={onClose}>
              Close
            </button>
            <button type="button" className="btn btn-primary" disabled={!dirty || saving} onClick={() => void save()}>
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </>
        ) : undefined
      }
    >
      <div className="drawer-form">
        <div className="person-facts">
          <Fact label="Status">
            <span className="pill" data-tone={STATUS[user.status].tone}>
              {STATUS[user.status].label}
            </span>
          </Fact>
          <Fact label="Email">{user.email_verified ? 'Confirmed' : 'Not confirmed yet'}</Fact>
          <Fact label="Signs in with">{signInMethods(user)}</Fact>
          <Fact label="Open sessions">{String(user.sessions)}</Fact>
          <Fact label="Last signed in" title={fullTime(user.last_login_at)}>
            {ago(user.last_login_at)}
          </Fact>
          <Fact label="Last active" title={fullTime(user.last_seen_at)}>
            {ago(user.last_seen_at)}
          </Fact>
          <Fact label="Added" title={fullTime(user.created_at)}>
            {ago(user.created_at)}
            {user.invited_by_email ? ` by ${user.invited_by_name || user.invited_by_email}` : ''}
          </Fact>
          {user.status === 'invited' ? (
            <Fact label="Invitation sent" title={fullTime(user.invited_at)}>
              {ago(user.invited_at)}
            </Fact>
          ) : null}
        </div>

        <label className="stack">
          <span className="label">Name</span>
          {canEditName ? (
            <input className="field" value={name} placeholder="Not given" onChange={(e) => setName(e.target.value)} />
          ) : (
            <span>{user.name || '—'}</span>
          )}
        </label>

        <div className="stack">
          <span className="label">Role</span>
          {mayAct ? (
            <RolePicker value={role} onChange={setRole} allowOwner={me.user?.role === 'owner'} />
          ) : (
            <span>
              {roleLabel(user.role)}
              {isMe ? <span className="hint"> Another owner or admin can change your role.</span> : null}
            </span>
          )}
        </div>

        {mayAct ? (
          <div className="stack">
            <span className="label">Access</span>
            <div className="person-actions">
              {user.status === 'invited' ? (
                <button type="button" className="btn" onClick={() => void actions.resend(user)}>
                  Send invitation again
                </button>
              ) : null}
              {user.status === 'active' ? (
                <button type="button" className="btn" onClick={() => void actions.reset(user)}>
                  Email a reset link
                </button>
              ) : null}
              <button type="button" className="btn" onClick={() => void actions.generate(user)}>
                Generate a password
              </button>
              {user.sessions > 0 ? (
                <button type="button" className="btn" onClick={() => void actions.signOut(user)}>
                  Sign out everywhere
                </button>
              ) : null}
              {user.status === 'active' ? (
                <button type="button" className="btn btn-danger" onClick={() => void actions.setStatus(user, 'disabled')}>
                  Disable
                </button>
              ) : null}
              {user.status === 'disabled' ? (
                <button type="button" className="btn" onClick={() => void actions.setStatus(user, 'active')}>
                  Turn back on
                </button>
              ) : null}
              <button type="button" className="btn btn-danger" onClick={() => void actions.remove(user)}>
                Remove from account
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </Drawer>
  );
}

function Fact({ label, title, children }: { label: string; title?: string; children: ReactNode }) {
  return (
    <div className="fact" title={title}>
      <span className="label">{label}</span>
      <span>{children}</span>
    </div>
  );
}
